import { ErrorBoundary, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import {
  AgentInterfaceSuggestionsProps,
  KnowledgeSuggestionsEvents,
} from 'lib-knowledge-types';
import { authManager } from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';
import { ampli } from 'lib-shared-tracking';
import { Suspense, useMemo } from 'react';

import { RemoteKnowledgeSuggestions } from 'app/views/layout/views/knowledge/components/remotes';
import { actions as personalActions } from 'components/settings/actions/personal';
import { useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { KnowledgeErrorPlaceholder } from '../../../elevio/src/components/KnowledgeErrorPlaceholder';
import { knowledgeActions } from '../../knowledge.actions';

import { knowledgeSelectors } from './knowledgeSelectors';
import styles from './knowledgeSuggestions.module.scss';

type KnowledgeSuggestionsProps = Pick<
  AgentInterfaceSuggestionsProps,
  'isMinimized'
>;

export function KnowledgeSuggestions({
  isMinimized,
}: KnowledgeSuggestionsProps): JSX.Element | null {
  const conversationId = useAppSelector(knowledgeSelectors.conversation.id);
  const conversationTags = useAppSelector(knowledgeSelectors.conversation.tags);
  const conversationText = useAppSelector(knowledgeSelectors.conversation.text);
  const queueId = useAppSelector(knowledgeSelectors.conversation.queueId);

  const notify = useNotifications();
  const dispatch = useAppDispatch();

  const { knowledgeAiDraft: hasSmartReply } = useFeatureFlag([
    'knowledge-ai-draft',
  ]);

  const knowledgeCardOpen = useAppSelector(
    (state) => state.settings.personal.knowledgeCardOpen,
  );

  function setKnowledgeCardOpen(newValue: boolean) {
    dispatch(
      personalActions.personalToggleRememberKnowledgeCardOpen({
        knowledgeCardOpen: newValue,
      }),
    );
  }

  const onHandlers = useMemo(() => {
    const handlers: KnowledgeSuggestionsEvents = {
      'article:viewed': (payload) => ampli.knowledgeArticleViewed(payload),
      'articles:searched-by-terms': (payload) =>
        ampli.knowledgeArticlesSearchByTermSubmitted(payload),
      'conversation:smart-reply-requested': ({ articleId, conversationId }) => {
        ampli.smartConversationReplyButtonClicked({
          articleId,
          conversationId,
          knowledgeSource: 'dixa',
        });
      },
      'conversation:smart-reply-response-received': ({ message }) => {
        dispatch(
          knowledgeActions.smartConversationResponseReceived({
            message,
          }),
        );
      },
    };
    return handlers;
  }, []);

  if (!conversationId) return null;

  return (
    <ErrorBoundary fallback={<KnowledgeErrorPlaceholder />}>
      <Suspense
        fallback={
          <FlexBox className={styles.spinner} justifyContent="center">
            <Spinner />
          </FlexBox>
        }
      >
        <RemoteKnowledgeSuggestions
          conversationId={conversationId}
          conversationTags={conversationTags}
          conversationText={conversationText}
          notify={notify}
          environment={config.environment}
          getLock={async () => await authManager.getAuthData()}
          isMinimized={isMinimized}
          isOpen={knowledgeCardOpen}
          setIsOpen={setKnowledgeCardOpen}
          queueId={queueId}
          on={onHandlers}
          hasSmartReply={hasSmartReply}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
